import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconContact, IconPlus, IconRefresh, IconSearch } from '../components/icons';
import { CodeTabs, Empty, Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { api, ApiError, AttributeDef, AttributeType, ConsentState, Contact, Paged, Segment } from '../lib/api';
import { ago, dateTime, num } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

export const TYPE_LABEL: Record<AttributeType, string> = {
  string: 'строка',
  number: 'число',
  boolean: 'да / нет',
  date: 'дата',
  datetime: 'дата и время',
  string_list: 'список строк',
};

/** Marketing consent: «Есть» / «Отозвано» / «Нет» (no record = no marketing). */
export function ConsentPill({ consent }: { consent: ConsentState }) {
  const s = consent.marketing?.status;
  const source = t('Источник: {source}', { source: consent.marketing?.source ?? '' });
  if (s === 'granted') return <span className="pill ok" title={source}>{t('Есть')}</span>;
  if (s === 'withdrawn') return <span className="pill bad" title={source}>{t('Отозвано')}</span>;
  return <span className="pill cancelled" title={t('Нет записи о согласии — маркетинг не отправляется')}>{t('Нет')}</span>;
}

export const isAdmin = (role?: string) => role === 'owner' || role === 'admin';

/** Error text with the first detail (path + hint), so a 422 explains itself. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.details?.[0];
    if (d) {
      const base = `${d.message}${d.hint ? ` — ${d.hint}` : ''}`;
      return d.did_you_mean ? t('{base} (может быть, {guess}?)', { base, guess: d.did_you_mean }) : base;
    }
    return err.message;
  }
  return (err as Error).message;
}

export function contactSnippets(key = 'mo_test_…') {
  const host = window.location.origin;
  return {
    cURL: `curl -X POST ${host}/v1/contacts \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "u_42",
    "email": "anna@example.com",
    "attributes": { "first_name": "Анна", "plan": "pro" },
    "consent": { "marketing": { "status": "granted", "source": "checkout_form" } }
  }'`,
    'Node.js': `import { MailOcean } from '@mailocean/sdk';
const mo = new MailOcean(process.env.MAILOCEAN_KEY);

await mo.contacts.upsert({
  external_id: user.id,
  email: user.email,
  attributes: { first_name: user.firstName, plan: user.plan },
  consent: { marketing: { status: 'granted', source: 'checkout_form' } },
});`,
    Python: `from mailocean import MailOcean
mo = MailOcean(os.environ["MAILOCEAN_KEY"])

mo.contacts.upsert(
    external_id=user.id,
    email=user.email,
    attributes={"first_name": user.first_name, "plan": user.plan},
    consent={"marketing": {"status": "granted", "source": "checkout_form"}},
)`,
    Go: `mo, _ := mailocean.New(os.Getenv("MAILOCEAN_KEY"))
_, err := mo.Contacts.Upsert(ctx, mailocean.ContactInput{
	ExternalID: user.ID,
	Email:      user.Email,
	Attributes: map[string]any{"first_name": user.FirstName, "plan": user.Plan},
}.WithMarketingConsent(mailocean.ConsentInput{Status: mailocean.ConsentGranted, Source: "checkout_form"}))`,
  };
}

export function ContactsPage() {
  const { me } = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [items, setItems] = useState<Contact[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const segments = useAsync(() => api.get<Segment[]>('/segments'));
  const segment = params.get('segment') ?? '';

  async function load(reset = true) {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50' });
      if (params.get('q')) p.set('q', params.get('q')!);
      if (segment) p.set('segment', segment);
      if (!reset && cursor) p.set('cursor', cursor);
      const res = await api.get<Paged<Contact>>(`/contacts?${p}`);
      setItems((xs) => (reset ? res.items : [...xs, ...res.items]));
      setCursor(res.next_cursor);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const filtered = Boolean(params.get('q') || segment);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Контакты')}</h1>
          <p>{t('Люди, которым вы пишете: идентификаторы, атрибуты, согласие на маркетинг и события. Профили не склеиваются сами — конфликт идентификаторов возвращается ошибкой.')}</p>
        </div>
        <button className="btn" onClick={() => load(true)}><IconRefresh size={15} /> {t('Обновить')}</button>
        {canWrite(me) && <button className="btn btn-primary" onClick={() => setCreating(true)}><IconPlus size={15} /> {t('Добавить')}</button>}
      </div>
      <div className="card">
        <div className="card-pad row wrap" style={{ borderBottom: '1px solid var(--border)' }}>
          <form className="row" style={{ flex: 1, minWidth: 220 }} onSubmit={(e) => (e.preventDefault(), setParam('q', q.trim()))}>
            <div style={{ position: 'relative', flex: 1 }}>
              <IconSearch size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--muted)' }} />
              <input className="input" style={{ paddingLeft: 36 }} placeholder={t('Email, начало адреса или external_id')} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </form>
          <select className="select input" style={{ width: 220, maxWidth: '100%' }} value={segment} onChange={(e) => setParam('segment', e.target.value)} aria-label={t('Сегмент')}>
            <option value="">{t('Все контакты')}</option>
            {segments.data?.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </div>
        {loading && !items.length ? (
          <div className="card-pad stack">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} />)}</div>
        ) : !items.length ? (
          filtered ? (
            <Empty icon={<IconContact size={36} />} title={t('Никого не нашли')}>{t('Поиск — по точному адресу, его началу (от 3 символов) или external_id.')}</Empty>
          ) : (
            <div className="card-pad stack">
              <Empty icon={<IconContact size={36} />} title={t('Контактов пока нет')}>{t('Передайте их из своего приложения — одним вызовом при регистрации или оформлении заказа. Повторный вызов обновляет контакт.')}</Empty>
              <CodeTabs snippets={contactSnippets()} />
            </div>
          )
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('Контакт')}</th><th>{t('Маркетинг')}</th><th>{t('Последнее событие')}</th><th>{t('Добавлен')}</th></tr></thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="click" onClick={() => nav(`/contacts/${c.id}`)}>
                    <td className="truncate" style={{ maxWidth: 320 }}>
                      <strong>{c.email ?? t('— без адреса —')}</strong>
                      {c.external_id && <div className="small muted truncate"><code>{c.external_id}</code></div>}
                    </td>
                    <td><ConsentPill consent={c.consent} /></td>
                    <td className="small muted">{c.last_event_at ? ago(c.last_event_at) : '—'}</td>
                    <td className="small muted" title={dateTime(c.created_at)} style={{ whiteSpace: 'nowrap' }}>{ago(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && (
          <div className="card-pad" style={{ textAlign: 'center', borderTop: '1px solid var(--border)' }}>
            <button className="btn" onClick={() => load(false)} disabled={loading}>{loading ? t('Загружаем…') : t('Загрузить ещё')}</button>
          </div>
        )}
      </div>
      <AttributeSchema />
      {creating && <CreateContact onClose={() => setCreating(false)} onCreated={(id) => nav(`/contacts/${id}`)} />}
    </>
  );
}

function CreateContact({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [externalId, setExternalId] = useState('');
  const [attrs, setAttrs] = useState('{\n  "first_name": ""\n}');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setError('');
    let attributes: Record<string, unknown> | undefined;
    if (attrs.trim()) {
      try {
        attributes = JSON.parse(attrs) as Record<string, unknown>;
        if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) throw new Error();
        attributes = Object.fromEntries(Object.entries(attributes).filter(([, v]) => v !== ''));
      } catch {
        setError(t('Атрибуты — JSON-объект, например { "plan": "pro" }'));
        return;
      }
    }
    setBusy(true);
    try {
      const r = await api.post<{ id: string; created: boolean }>('/contacts', { ...(email ? { email } : {}), ...(externalId ? { external_id: externalId } : {}), ...(attributes ? { attributes } : {}) });
      toast(r.created ? t('Контакт добавлен') : t('Такой контакт уже был — обновили'));
      onCreated(r.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('Новый контакт')}
      onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-primary" onClick={save} disabled={busy || (!email && !externalId)}>{t('Сохранить')}</button></>}
    >
      <Field label="Email"><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="anna@example.com" /></Field>
      <Field label="external_id" hint={t('id пользователя в вашей системе; нужен email или external_id')}><input className="input input-mono" value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder="u_42" /></Field>
      <Field label={t('Атрибуты (JSON)')} hint={t('Новый ключ получает тип по первому значению')} error={error || undefined}>
        <textarea className="input input-mono" rows={5} value={attrs} onChange={(e) => setAttrs(e.target.value)} />
      </Field>
      <div className="callout">{t('Согласие на маркетинг здесь не ставится: его записывают с источником и доказательством в карточке контакта или через API.')}</div>
    </Modal>
  );
}

/** Typed attribute schema of the workspace; owners and admins declare keys (e.g. a date) before the first value. */
function AttributeSchema() {
  const { me } = useSession();
  const toast = useToast();
  const schema = useAsync(() => api.get<{ items: AttributeDef[]; limit: number }>('/contacts/attributes'));
  const [key, setKey] = useState('');
  const [type, setType] = useState<AttributeType>('date');
  const admin = isAdmin(me?.user.role) && !me?.support;

  async function add(e: FormEvent) {
    e.preventDefault();
    try {
      await api.put(`/contacts/attributes/${encodeURIComponent(key.trim())}`, { type });
      toast(t('Ключ объявлен'));
      setKey('');
      schema.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>{t('Схема атрибутов')}</h3>
        {schema.data && <span className="small muted">{t('{a} из {b} ключей', { a: num(schema.data.items.length), b: num(schema.data.limit) })}</span>}
      </div>
      {admin && (
        <form className="card-pad row wrap" style={{ borderBottom: '1px solid var(--border)' }} onSubmit={add}>
          <input className="input input-mono" style={{ flex: 1, minWidth: 160 }} placeholder="signup_date" value={key} onChange={(e) => setKey(e.target.value)} aria-label={t('Ключ')} />
          <select className="select input" style={{ width: 170 }} value={type} onChange={(e) => setType(e.target.value as AttributeType)} aria-label={t('Тип')}>
            {(Object.keys(TYPE_LABEL) as AttributeType[]).map((ty) => <option key={ty} value={ty}>{t(TYPE_LABEL[ty])}</option>)}
          </select>
          <button className="btn" disabled={!key.trim()}>{t('Объявить')}</button>
        </form>
      )}
      {schema.loading ? (
        <div className="card-pad"><Skeleton /></div>
      ) : !schema.data?.items.length ? (
        <div className="card-pad small muted">{t('Ключи появятся с первыми контактами. Строка, похожая на дату, остаётся строкой — дату объявите заранее.')}</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('Ключ')}</th><th>{t('Тип')}</th><th>{t('Подпись')}</th><th>{t('С')}</th></tr></thead>
            <tbody>
              {schema.data.items.map((a) => (
                <tr key={a.key}>
                  <td><code>{a.key}</code></td>
                  <td>{t(TYPE_LABEL[a.type])}</td>
                  <td className="small muted">{a.label ?? '—'}</td>
                  <td className="small muted">{ago(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
